// Import Packages
import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilState } from 'recoil';

// Import Pages
import DiagramBuilderIcon from '../../../../../../assets/icons/DiagramBuilder';
import Calculator from '../../../../../../assets/icons/Calculator';
import ShortCutSection from '../../shortCutSection/ShortCutSection';
import { handleRedo, handleUndo, handleUpdate } from '../../../../../../helpers/flowHistory';
import { alertState } from '../../../../../../recoil-store/recoil-state';
import TabDialog from '../detailTabs/TabDialog';
import Item from './item/Item';
import TriggerModal from '../triggerModal/TriggerModal';


// Import Icons
import RoiIcon from '../../../../../../assets/icons/roi-icon.svg'
import DiagramIcon from '../../../../../../assets/icons/diagram-icon.svg'

// Import CSS
import './DropArea.css';
import { changeOrder } from "../../../../../../helpers/helper";
import VariableService from "../../../../../../services/VariableService/VariableService";

const DropArea = ({
    automDetail,
    setAutomDetail,
    handleDragStart,
    isFocused,
    setDetailTabs,
    debuggingStart,
    openRunningModal,
    setShowRightArea,
    setShowTriggerRightArea,
    setDebuggingExpand,
    debuggingExpand, handleDragEnd,
    handleRoi, handleDrag,
    detailTabs, handleDrop,
    handleDiagramBuilder,
    findAndUpdateItem,
    handleDebugIconShow,
    handleExport,
    handleImport,
    handleSlowMotion,
    handleErrorOpenClick,
    handleErrorCloseClick,
    variables,
    setOpenVariable,
    action,
    reCheck,
    setReCheck,
    openResetTriggerConfiguration,
    automaticallyVariableCreation,
    setTriggerModal,
    setOpenSchedule,
    automaticallyWhatsappVariableCreation,
    getVariables,
    updateVariableByItemId
}) => {
    const { t } = useTranslation();
    const [scale, setScale] = useState(1);
    const [dialog, setDialog] = useState(null);
    const [triggerOffSet, setTriggerOffSet] = useState(null);
    const [openTriggerConfigure, setOpenTriggerConfigure] = useState(false);

    const setAlert = useSetRecoilState(alertState);

    const addItem = (id, items, newItem, direction, directionFlag, isFirstChild, isMove, bulk) => {
        items.forEach((item, index) => {
            if (item.id === id) {
                if (isFirstChild) {
                    if (item.children) {
                        item.children = [newItem, ...item.children];
                    } else {
                        item.children = [newItem];
                    }
                } else {
                    if (newItem.name === 'ELSE' && items.length - 1 !== index) return false;
                    items.splice(index + 1, 0, { ...newItem, conditionGroupId: item.conditionGroupId });
                }
            }
            if (directionFlag && item.children && item.children[0]?.id === id) {
                item.flow = direction;
            }
            if (item.id !== id && item.children) {
                addItem(id, item.children, newItem, direction, directionFlag, isFirstChild, isMove);
            }
        });
        changeOrder(items, 0);
        if (newItem.name === 'CONDITION') newItem = newItem.children[0];
        if (!bulk && !isMove)
            setAutomDetail({
                ...automDetail, items, activeItem: [{
                    ...newItem,
                    actionName: newItem.action_name,
                    libraryName: newItem.library_name,
                    libraryLabel: newItem.library_label,
                }]
            });
        return true;
    };

    function removeItem(id, items, i) {
        return items.map(item => {
            return { ...item };
        }).filter(item => {
            if ('children' in item) {
                if (i && item.children.find(x => x.id === id)) return false;
                item.children = removeItem(id, item.children, i);
            }
            return item.id !== id;
        });
    }

    function RemoveAndActiveItem(id, items, action, node = {}) {
        items.map((item, k) => {
            if (item.id === id) {
                if (items[k - 1]) {
                    action.push(items[k - 1]);
                } else {
                    action.push(node);
                }
            }
            if ('children' in item) {
                RemoveAndActiveItem(id, item.children, action, item);
            }
            return item;
        });
    }

    const checkUpdateRules = (index, prevItem, draggingItem, direction, isFirstChild, item, id, group) => {
        // TODO CHECK AND HANDLE THIS METHOD

        if (draggingItem === 'START' || draggingItem === 'STOP') {
            return false; // rule: start and stop cannot be draggable item
        }

        if (group?.action_name === 'LIST-SCOPE' && draggingItem !== 'LIST-ITEM') {
            return false; // rule: start and stop cannot be draggable item
        }

        if (group?.action_name === 'BUTTON-SCOPE' && draggingItem !== 'BUTTON-ITEM') {
            return false; // rule: start and stop cannot be draggable item
        }

        if (draggingItem === 'LIST-ITEM' && group?.action_name === 'LIST-SCOPE' && group?.children?.length > 9) {
            return false; // rule: start and stop cannot be draggable item
        }

        if (draggingItem === 'BUTTON-ITEM' && group?.action_name === 'BUTTON-SCOPE' && group?.children?.length > 2) {
            return false; // rule: start and stop cannot be draggable item
        }


        if ((draggingItem === 'FIND-AND-INPUT' ||
            draggingItem === 'FIND-AND-CLICK' ||
            draggingItem === 'GET-TEXT' ||
            draggingItem === 'GET-IMAGE' ||
            draggingItem === 'HOVER') && group?.action_name !== 'SCOPE') {
            return false; // rule: start and stop cannot be draggable item
        }

        if (item.id === id) {
            return true; // rule: groupable element cannot be enclosed
        }

        if (direction === 'column' && draggingItem !== 'ELSE' && draggingItem !== 'ELSEIF' && draggingItem !== 'LIST-ITEM' && draggingItem !== 'BUTTON-ITEM') {
            return false; // rule: elements other than else&elseif cannot be added to horizontal line
        }

        if (isFirstChild && (draggingItem === 'ELSE' || draggingItem === 'ELSEIF')) {
            return false; // rule: else and elseif cannot be added as a first child
        }

        if (!isFirstChild && (prevItem === 'IF' || prevItem === 'ELSEIF') && draggingItem !== 'ELSE' && draggingItem !== 'ELSEIF') {
            return false; // rule:
        }

        if (prevItem !== 'IF' && prevItem !== 'ELSEIF' && (draggingItem === 'ELSE' || draggingItem === 'ELSEIF')) {
            return false; // rule:
        }

        if (item.type === 'groupable' && item.children) {
            let flatten = item.children.flat();
            if (flatten.findIndex(element => element.id === id) > -1)
                return false; // rule: parent element cannot be moved into its own child
        }

        return true;
    };

    const updateIDs = (items) => {
        if (Array.isArray(items)) {
            for (const item of items) {
                //item.item_id = uuidv4();
                item.id = uuidv4();
                if (item.children?.length > 0) {
                    updateIDs(items.children);
                }
            }
        }
    };

    const updateItems = (index, name, id, direction, isFirstChild, copy, bulk, modified, group) => {
        setShowTriggerRightArea(false);
        let currentItem;
        if (copy) {
            currentItem = JSON.parse(localStorage.getItem('copiedItem'));
        } else {
            currentItem = JSON.parse(localStorage.getItem('dragItem'));
        }
        let item = bulk || currentItem?.item[0] || currentItem?.item; // dragging item
        let isMove = currentItem?.isMove;
        let directionFlag = false;
        if (item) {
            let addableFlag = checkUpdateRules(index, name, item.action_name, direction, isFirstChild, item, id, group);
            if (addableFlag) {
                let items = modified ? modified : JSON.parse(JSON.stringify(automDetail.items));
                let newItem = {
                    ...item,
                    name: item.action_name,
                    type: item.type ? item.type : item.flow_behavior?.includes('groupable') ? 'groupable' : 'standard',
                    // item_id: uuidv4(),
                    id: uuidv4(),
                    hidden: false
                };

                if (newItem?.fields) {
                    if (newItem.library_label === "WHATSAPP" && (newItem.action_label === "LISTITEM" || newItem.action_label === "BUTTONITEM")) {
                        const variableName = `##${newItem.action_label.toLowerCase()}_${newItem.item_id.replaceAll('-', '')}##`
                        automaticallyWhatsappVariableCreation(newItem.item_id, newItem.action_label)

                        newItem.fields.set = {
                            conditions: [],
                            value: variableName
                        }
                    }

                    newItem = findAndUpdateItem(newItem, isMove, copy);
                }

                if (newItem.name === 'CONDITION', copy) {
                    updateIDs(newItem.children);
                }

                if (['IF', 'ELSE', 'ELSEIF'].includes(newItem.name)) {

                    // Todo HANDLE CONDITION GROUP CREATION

                    if (newItem.name === 'IF') {
                        let conditionGroupId = uuidv4();
                        newItem.conditionGroupId = conditionGroupId;
                        let conditionItem = {
                            action_name: 'CONDITION',
                            name: 'CONDITION',
                            type: 'condition',
                            // item_id: uuidv4(),
                            id: uuidv4(),
                            conditionGroupId: conditionGroupId,
                            flow: 'rowCol',
                            children: [newItem],
                            flow_behavior: ['vertical', 'groupable'],
                            hidden: false
                        };
                        newItem = conditionItem;
                    } else {
                        directionFlag = true;
                    }
                }
                if (addItem(id, items, newItem, direction, directionFlag, isFirstChild, isMove, bulk)) {
                    if (!isMove) handleUpdate(items);
                    addableFlag = true;
                } else addableFlag = false;

                if (addableFlag && isMove) {
                    let modifiedItems = removeItem(item.id, items);
                    if (bulk) items = modifiedItems;
                    else {
                        setAutomDetail({ ...automDetail, items: modifiedItems, activeItem: [newItem] });
                        handleUpdate(modifiedItems);
                    }
                }

                if (bulk) return items;
            }
            // localStorage.removeItem("dragItem");
        }
    };

    const deleteItem = (item, cut) => {
        // let newActiveItem = [];
        let items = JSON.parse(JSON.stringify(automDetail.items));
        // const previousOrder = item.order - 1;
        // let selectedAction = [];
        // RemoveAndActiveItem(item.id, items, selectedAction);
        let modifiedItems = items;
        if (Array.isArray(item)) {
            for (const i of item) {
                modifiedItems = removeItem(i.id, modifiedItems, i.action_name === 'IF');
            }
        } else {
            modifiedItems = removeItem(item.id, modifiedItems, item.action_name === 'IF');
        }

        // const previousAction = automDetail.items.find(x => x.order === previousOrder)
        // if (previousAction.actionName !== 'START') {
        //     newActiveItem = previousAction;
        // }

        setAutomDetail({ ...automDetail, items: modifiedItems, activeItem: [] });
        handleUpdate(modifiedItems);
        if (['GROUP', 'REPEAT', 'CONDITION', 'IF', 'ELSEIF', 'ELSE'].includes(item.action_name)) {
            setDetailTabs(prev => prev.filter(i => i.id !== item.id));
        }
        if (!cut) {
            localStorage.removeItem('dragItem');
        }
    };

    const setActiveItem = (e, item, type = 'normal') => {
        if (type === 'debug') {
            let items = JSON.parse(JSON.stringify(automDetail.items));
            let active = JSON.parse(JSON.stringify(item));
            let modifiedItems = handleDebugIconShow(item.id, items);
            setAutomDetail({
                ...automDetail, items: modifiedItems, activeItem: [{
                    ...active,
                    actionName: active.action_name,
                    libraryName: active.library_name,
                    libraryLabel: active.library_label,
                    isDebugging: !active.isDebugging,
                }]
            });
        } else {
            let activeItem, exist, selected = {
                ...item,
                actionName: item.action_name,
                libraryName: item.library_name,
                libraryLabel: item.library_label,
            };

            if (e.ctrlKey) {
                activeItem = automDetail.activeItem.filter(i => {
                    if (i.id === item.id) {
                        exist = true;
                        return false;
                    } else return true;
                });
                if (!exist) activeItem.push(selected);
            } else activeItem = [selected];
            if (!(activeItem?.length > 0)) setShowRightArea(false);
            setAutomDetail({ ...automDetail, activeItem });
        }

    };

    const getSelectedItems = () => {
        return automDetail.activeItem;
    }

    const changeScale = (e, action) => {
        if (action === 'zoom-in' && scale < 1) {
            setScale(scale + 0.15);
        } else if (action === 'zoom-out' && scale > 0.35) {
            setScale(scale - 0.15);
        }
    };

    const handleForward = () => {
        const next = handleRedo();
        if (next) {
            setAutomDetail({ ...automDetail, items: next });
        }
    };

    const handleBack = () => {
        const prev = handleUndo();
        if (prev) {
            setAutomDetail({ ...automDetail, items: prev });
        }
    };

    const handlePaste = () => {
        if (automDetail.activeItem[0] && automDetail.activeItem[0].name !== 'STOP') {
            let currentItem = JSON.parse(localStorage.getItem('copiedItem'))?.item;
            let modified = JSON.parse(JSON.stringify(automDetail.items));
            if (!Array.isArray(currentItem)) currentItem = [currentItem];
            for (const item of currentItem) {
                if (['REPEAT', 'GROUP', 'IF', 'ELSE'].includes(automDetail.activeItem[0]?.name)) {
                    modified = updateItems(0, automDetail.activeItem[0].name, automDetail.activeItem[0].id, undefined, true, true, item, modified);
                } else {
                    modified = updateItems(0, automDetail.activeItem[0]?.name, automDetail.activeItem[0]?.id, undefined, undefined, true, item, modified);
                }
            }
            if (modified) {
                handleUpdate(modified);
                setAutomDetail({ ...automDetail, items: modified });
            }
        }
    }

    const handleDelete = () => {
        const activeItem = automDetail.activeItem.length > 0 ? automDetail.activeItem[0] : undefined;
        if (activeItem && activeItem.libraryLabel !== "TRIGGERSTATIC" && activeItem.libraryLabel !== "STOP" && activeItem.actionName !== "START") {
            deleteItem(automDetail.activeItem);
            setShowRightArea(false);
        }
    }

    const handleCut = (event) => {
        if (automDetail.activeItem && automDetail.activeItem.length && automDetail.activeItem[0].name !== 'STOP' && automDetail.activeItem[0].name !== 'START') {
            handleDragStart(event, automDetail.activeItem, true, true);
            deleteItem(automDetail.activeItem, true);
        }
    }

    const handleCopy = (event) => {
        handleDragStart(event, automDetail.activeItem, undefined, true);
        setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.AUTOMS.AUTOM-COPIED'), severity: 'success' }));
    }

    function handleKeyDown(event) {
        let charCode = String.fromCharCode(event.which).toLowerCase();
        if (event.ctrlKey && event.shiftKey && charCode === 'v') {
        } else if (event.ctrlKey && charCode === 'v') {
            handlePaste();
        } else if (event.keyCode === 46) {
            handleDelete();
        } else if (event.ctrlKey && charCode === 'x') {
            handleCut(event);
        } else if (event.ctrlKey && charCode === 'c') {
            handleCopy(event);
        } else {
            return;
        }
    }

    return (
        <>
            <button onClick={handleDiagramBuilder} className="diagram-flow-button">
                <img src={DiagramIcon} alt="" />
                <span>{t('CONTENT.FLOW.DIAGRAM')}</span>
            </button>
            <button onClick={handleRoi} className="diagram-flow-button roi-button">
                <img src={RoiIcon} alt="" />
                <span>{t('CONTENT.FLOW.ROI')}</span>
            </button>
            <ShortCutSection changeScale={changeScale} onForward={handleForward} onBack={handleBack}
                onDelete={handleDelete} onCut={handleCut} onCopy={handleCopy} onPaste={handlePaste}
                handleExport={handleExport} handleImport={handleImport} handleSlowMotion={handleSlowMotion}
                isDebuggingExpand={debuggingExpand} setDebuggingExpand={setDebuggingExpand}
                isDebugging={debuggingStart} openRunningModal={openRunningModal} />

            <div id="flow-container" onDragOver={e => e.preventDefault()} onDrop={handleDrop} className="drop-container"
                onDragEnd={handleDragEnd}>
                {
                    !openTriggerConfigure && (
                        <div id="new-flow-drop-area-grid" className="new-flow-drop-area-grid"
                            style={{ transform: `scale(${scale})` }}
                            tabIndex="0" onKeyDown={handleKeyDown} onDragStart={handleDrag} draggable>
                            {automDetail.items &&
                                automDetail.items.map((item, index) => (
                                    !item?.hidden &&
                                    <Item key={item.id} item={item} index={index} count={automDetail.items.length}
                                        setDetailTabs={setDetailTabs} debuggingStart={debuggingStart}
                                        updateItems={updateItems} handleDragStart={handleDragStart}
                                        setActiveItem={setActiveItem} setDialog={setDialog}
                                        deleteItem={deleteItem} isConditionChild={false}
                                        isFocused={isFocused} detailTabs={detailTabs}
                                        getSelectedItems={getSelectedItems}
                                        automDetail={automDetail}
                                        setAutomDetail={setAutomDetail}
                                        handleErrorOpenClick={handleErrorOpenClick}
                                        handleErrorCloseClick={handleErrorCloseClick}
                                        setShowTriggerRightArea={setShowTriggerRightArea}
                                        setOpenTriggerConfigure={setOpenTriggerConfigure}
                                        setTriggerOffSet={setTriggerOffSet}
                                        setReCheck={setReCheck} reCheck={reCheck}
                                        openResetTriggerConfiguration={openResetTriggerConfiguration}
                                        automaticallyVariableCreation={automaticallyVariableCreation}
                                        setTriggerModal={setTriggerModal} setOpenSchedule={setOpenSchedule}
                                        automaticallyWhatsappVariableCreation={automaticallyWhatsappVariableCreation}
                                        getVariables={getVariables}
                                        updateVariableByItemId={updateVariableByItemId}
                                    />
                                ))}
                        </div>
                    )}

            </div>
            {
                triggerOffSet && (
                    <TriggerModal automId={automDetail.id} automVersionId={automDetail.versionId}
                        openTriggerConfigure={openTriggerConfigure} setAutomDetail={setAutomDetail}
                        setOpenTriggerConfigure={setOpenTriggerConfigure} triggerOffSet={triggerOffSet}
                        variables={variables} setOpenVariable={setOpenVariable} action={action}
                        automDetail={automDetail} reCheck={reCheck} setReCheck={setReCheck} />
                )
            }
            {dialog && <TabDialog open={dialog} setOpen={setDialog} setDetailTabs={setDetailTabs} />}
        </>
    );
};
export default DropArea;
